import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SHEET_POSITIONS,
  effectiveCaptain,
  effectiveNumber,
  effectivePosition,
  fieldIssue,
  fullName,
  needsPlayerResolution,
  rowChips,
  type ChipKind,
  type ClashItem,
  type ReviewRowState,
  type RowSection,
  type RowEdits,
  type RowResolution,
} from "../../lib/matchSheetImportReview";

const CHIP_STYLE: Record<ChipKind, string> = {
  exact: "bg-green-100 text-green-800",
  candidates: "bg-amber-100 text-amber-800",
  none: "bg-red-100 text-red-800",
  unreadable: "bg-red-100 text-red-800",
  disagreement: "bg-orange-100 text-orange-800",
  invalid: "bg-red-100 text-red-800",
  cross_sheet: "bg-orange-100 text-orange-800",
};

interface Props {
  sheetIndex: 0 | 1;
  rows: ReviewRowState[];
  duplicates: Set<string>;
  teamReady: boolean;
  /** Row keys (this sheet only) with a re-match in flight. */
  rematching: Set<string>;
  /** Row errors for THIS sheet only, keyed by row key. */
  errors: Record<string, string>;
  /** Server/clash error from Continue for THIS sheet, keyed by row key. */
  createErrors: Record<string, string>;
  clashes: Map<string, ClashItem>;
  duplicateRows: Set<string>;
  onSection: (key: string, section: RowSection) => void;
  onEdit: (key: string, edits: RowEdits) => void;
  onNameCommit: (key: string, pending?: RowEdits) => void;
  onResolve: (key: string, resolution: RowResolution) => void;
}

type TextKey = "first_name" | "surname_1" | "surname_2";

export default function SheetRowsTable({
  sheetIndex,
  rows,
  duplicates,
  teamReady,
  rematching,
  errors,
  createErrors,
  clashes,
  duplicateRows,
  onSection,
  onEdit,
  onNameCommit,
  onResolve,
}: Props) {
  const { t } = useTranslation("admin");
  const [confirmingCreate, setConfirmingCreate] = useState<string | null>(null);
  const tid = (key: string) => `sheet-row-${sheetIndex}-${key}`;

  const textValue = (r: ReviewRowState, k: TextKey): string => {
    const edit = r.edits[k];
    if (edit !== undefined) return edit;
    const f = r.source[k];
    return f.status === "ok" ? (f.value ?? "") : "";
  };

  const readingsOf = (r: ReviewRowState, k: TextKey | "position" | "number") =>
    r.source[k].status === "disagreement" ? (r.source[k].readings ?? []) : [];

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full text-sm"
        data-testid={`sheet-rows-${sheetIndex}`}
      >
        <thead>
          <tr className="text-left text-xs uppercase text-gray-500">
            <th className="px-1">#</th>
            <th className="px-1">{t("matchSheetImport.col.number")}</th>
            <th className="px-1">{t("matchSheetImport.col.firstName")}</th>
            <th className="px-1">{t("matchSheetImport.col.surname1")}</th>
            <th className="px-1">{t("matchSheetImport.col.surname2")}</th>
            <th className="px-1">{t("matchSheetImport.col.position")}</th>
            <th className="px-1">{t("matchSheetImport.col.captain")}</th>
            <th className="px-1">{t("matchSheetImport.col.section")}</th>
            <th className="px-1">{t("matchSheetImport.col.status")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const removed = r.resolution.kind === "removed";
            const chips = rowChips(r, duplicates);
            const needsPlayer = needsPlayerResolution(r) && !removed;
            const bad = (k: Parameters<typeof fieldIssue>[1]) =>
              !removed && fieldIssue(r, k, duplicates) !== null;
            const cls = (k: Parameters<typeof fieldIssue>[1]) =>
              `input w-full text-sm ${bad(k) ? "border-red-500 bg-red-50" : ""}`;
            const num = effectiveNumber(r);
            const clash = removed ? undefined : clashes.get(r.key);
            const failed = !!createErrors[r.key];
            return (
              <tr
                key={r.key}
                className={`border-t align-top ${removed ? "opacity-50" : ""} ${
                  failed || clash || duplicateRows.has(r.key) ? "bg-red-50" : ""
                }`}
                data-failed={failed ? "true" : undefined}
                data-testid={tid(r.key)}
              >
                <td className="px-1 py-1 text-gray-400">{r.source.row}</td>
                <td className="px-1 py-1 w-16">
                  <input
                    type="number"
                    min={1}
                    max={99}
                    disabled={removed}
                    value={num ?? ""}
                    onChange={(e) =>
                      onEdit(r.key, {
                        number:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    aria-label={t("matchSheetImport.col.number")}
                    className={cls("number")}
                    data-testid={`${tid(r.key)}-number`}
                  />
                  {readingsOf(r, "number").map((v, i) => (
                    <button
                      key={i}
                      type="button"
                      className="block text-xs text-blue-700 underline"
                      onClick={() =>
                        onEdit(r.key, { number: Number(v) || null })
                      }
                    >
                      {v ?? "-"}
                    </button>
                  ))}
                </td>
                {(["first_name", "surname_1", "surname_2"] as TextKey[]).map(
                  (k) => (
                    <td key={k} className="px-1 py-1">
                      <input
                        type="text"
                        disabled={removed}
                        value={textValue(r, k)}
                        onChange={(e) => onEdit(r.key, { [k]: e.target.value })}
                        onBlur={() => onNameCommit(r.key)}
                        aria-label={t(
                          `matchSheetImport.col.${k === "first_name" ? "firstName" : k === "surname_1" ? "surname1" : "surname2"}`,
                        )}
                        className={cls(k)}
                        data-testid={`${tid(r.key)}-${k}`}
                      />
                      {readingsOf(r, k).map((v, i) => (
                        <button
                          key={i}
                          type="button"
                          className="block text-xs text-blue-700 underline"
                          data-testid={`${tid(r.key)}-${k}-reading-${i}`}
                          onClick={() => {
                            onEdit(r.key, { [k]: v ?? "" });
                            onNameCommit(r.key, { [k]: v ?? "" });
                          }}
                        >
                          {v ?? "-"}
                        </button>
                      ))}
                      {k === "surname_2" && bad("surname_2") && (
                        <button
                          type="button"
                          className="block text-xs text-gray-500 underline"
                          data-testid={`${tid(r.key)}-surname_2-clear`}
                          onClick={() => {
                            onEdit(r.key, { surname_2: "" });
                            onNameCommit(r.key, { surname_2: "" });
                          }}
                        >
                          {t("matchSheetImport.leaveEmpty")}
                        </button>
                      )}
                    </td>
                  ),
                )}
                <td className="px-1 py-1 w-24">
                  <select
                    disabled={removed}
                    value={effectivePosition(r) ?? ""}
                    onChange={(e) =>
                      onEdit(r.key, { position: e.target.value })
                    }
                    aria-label={t("matchSheetImport.col.position")}
                    className={cls("position")}
                    data-testid={`${tid(r.key)}-position`}
                  >
                    <option value="">-</option>
                    {SHEET_POSITIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-1 py-1">
                  <input
                    type="checkbox"
                    disabled={removed}
                    checked={effectiveCaptain(r)}
                    onChange={(e) =>
                      onEdit(r.key, { captain: e.target.checked })
                    }
                    aria-label={t("matchSheetImport.col.captain")}
                    className={
                      bad("flags") ? "outline outline-2 outline-red-500" : ""
                    }
                    data-testid={`${tid(r.key)}-captain`}
                  />
                </td>
                <td className="px-1 py-1">
                  <select
                    disabled={removed}
                    value={r.section}
                    onChange={(e) =>
                      onSection(r.key, e.target.value as RowSection)
                    }
                    aria-label={t("matchSheetImport.col.section")}
                    className="input w-full text-sm"
                    data-testid={`${tid(r.key)}-section`}
                  >
                    <option value="starters">{t("starters")}</option>
                    <option value="substitutes">{t("substitutes")}</option>
                  </select>
                </td>
                <td className="px-1 py-1 min-w-[16rem]">
                  <div className="flex flex-wrap gap-1">
                    {removed ? (
                      <span
                        className="rounded px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700"
                        data-testid={`sheet-row-chip-${sheetIndex}-${r.key}-removed`}
                      >
                        {t("matchSheetImport.chip.removed")}
                      </span>
                    ) : (
                      chips.map((c) => (
                        <span
                          key={c}
                          className={`rounded px-1.5 py-0.5 text-xs ${CHIP_STYLE[c]}`}
                          data-testid={`sheet-row-chip-${sheetIndex}-${r.key}-${c}`}
                        >
                          {t(`matchSheetImport.chip.${c}`)}
                        </span>
                      ))
                    )}
                    {!removed && r.playerMatch.jersey_mismatch && (
                      <span
                        className="rounded px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800"
                        data-testid={`sheet-row-chip-${sheetIndex}-${r.key}-jersey`}
                      >
                        {t("matchSheetImport.chip.jerseyMismatch")}
                      </span>
                    )}
                    {r.resolution.kind === "player" && (
                      <span
                        className="rounded px-1.5 py-0.5 text-xs bg-green-100 text-green-800"
                        data-testid={`${tid(r.key)}-picked`}
                      >
                        {r.resolution.name}
                      </span>
                    )}
                    {r.resolution.kind === "create" && (
                      <span
                        className="rounded px-1.5 py-0.5 text-xs bg-purple-100 text-purple-800"
                        data-testid={`${tid(r.key)}-will-create`}
                      >
                        {t("matchSheetImport.chip.willCreate")}
                      </span>
                    )}
                  </div>

                  {needsPlayer && (
                    <div
                      className="mt-1 space-y-1"
                      data-testid={`${tid(r.key)}-resolve`}
                    >
                      {!teamReady && (
                        <p className="text-xs text-gray-500">
                          {t("matchSheetImport.pickTeamFirst")}
                        </p>
                      )}
                      {r.playerMatch.candidates
                        .filter((c) => c.on_team)
                        .map((c) => (
                          <div
                            key={c.player_id}
                            className="flex flex-wrap items-center gap-1 text-xs"
                            data-testid={`${tid(r.key)}-candidate-${c.player_id}`}
                          >
                            <span>
                              {c.name}
                              {c.jersey_number != null
                                ? ` #${c.jersey_number}`
                                : ""}{" "}
                              ({Math.round(c.score * 100)}%)
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              data-testid={`${tid(r.key)}-pick-${c.player_id}`}
                              onClick={() =>
                                onResolve(r.key, {
                                  kind: "player",
                                  playerId: c.player_id,
                                  name: c.name,
                                })
                              }
                            >
                              {t("matchSheetImport.pick")}
                            </button>
                          </div>
                        ))}
                      {confirmingCreate === r.key ? (
                        <div
                          className="text-xs"
                          data-testid={`${tid(r.key)}-create-confirm`}
                        >
                          <p>
                            {t("matchSheetImport.createConfirm", {
                              name: fullName(r),
                              number: num ?? "-",
                              position: effectivePosition(r) ?? "-",
                            })}
                          </p>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary mr-1"
                            data-testid={`${tid(r.key)}-create-yes`}
                            onClick={() => {
                              setConfirmingCreate(null);
                              onResolve(r.key, { kind: "create" });
                            }}
                          >
                            {t("matchSheetImport.createYes")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => setConfirmingCreate(null)}
                          >
                            {t("cancel")}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          disabled={
                            !teamReady ||
                            !fullName(r) ||
                            fieldIssue(r, "number", duplicates) !== null ||
                            fieldIssue(r, "position", duplicates) !== null
                          }
                          data-testid={`${tid(r.key)}-create`}
                          onClick={() => setConfirmingCreate(r.key)}
                        >
                          {t("matchSheetImport.createProvisional")}
                        </button>
                      )}
                    </div>
                  )}
                  {rematching.has(r.key) && (
                    <p
                      className="mt-1 text-xs text-gray-500"
                      role="status"
                      data-testid={`${tid(r.key)}-rematching`}
                    >
                      {t("matchSheetImport.rematching")}
                    </p>
                  )}
                  {clash && (
                    <div
                      className="mt-1 text-xs text-red-700"
                      data-testid={`${tid(r.key)}-clash`}
                    >
                      <p>
                        {t("matchSheetImport.clash", {
                          number: clash.number,
                          name: clash.name,
                        })}
                      </p>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary mt-1"
                        data-testid={`${tid(r.key)}-clash-pick`}
                        onClick={() =>
                          onResolve(r.key, {
                            kind: "player",
                            playerId: clash.playerId,
                            name: clash.name,
                          })
                        }
                      >
                        {t("matchSheetImport.clashPick", { name: clash.name })}
                      </button>
                    </div>
                  )}
                  {createErrors[r.key] && (
                    <p
                      className="mt-1 text-xs font-medium text-red-700"
                      role="alert"
                      data-testid={`${tid(r.key)}-create-error`}
                    >
                      {createErrors[r.key]}
                    </p>
                  )}
                  {errors[r.key] && (
                    <p
                      className="mt-1 text-xs text-red-700"
                      data-testid={`${tid(r.key)}-error`}
                    >
                      {errors[r.key]}
                    </p>
                  )}
                  <div className="mt-1 flex gap-2 text-xs">
                    {(r.resolution.kind === "player" ||
                      r.resolution.kind === "create") && (
                      <button
                        type="button"
                        className="text-blue-700 underline"
                        data-testid={`${tid(r.key)}-change`}
                        onClick={() => onResolve(r.key, { kind: "none" })}
                      >
                        {t("matchSheetImport.change")}
                      </button>
                    )}
                    {removed ? (
                      <button
                        type="button"
                        className="text-blue-700 underline"
                        data-testid={`${tid(r.key)}-restore`}
                        onClick={() => onResolve(r.key, { kind: "none" })}
                      >
                        {t("matchSheetImport.restoreRow")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="text-red-700 underline"
                        data-testid={`${tid(r.key)}-remove`}
                        onClick={() => onResolve(r.key, { kind: "removed" })}
                      >
                        {t("matchSheetImport.removeRow")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
